import { useEffect, useRef, useState, useCallback } from "react";
import Lenis from "lenis";
import { Toaster } from "sonner";
import "@/App.css";
import { loadConcepts } from "./lib/concepts";
import { LangProvider } from "./lib/i18n";
import { Nav } from "./components/Nav";
import { Hero } from "./components/Hero";
import { Marquee } from "./components/Marquee";
import { Matcher } from "./components/Matcher";
import { Library } from "./components/Library";
import { ConceptOverlay } from "./components/ConceptOverlay";
import { Method } from "./components/Method";
import { Positioning } from "./components/Positioning";
import { ArabicCreed } from "./components/ArabicCreed";
import { Handover } from "./components/Handover";
import { Contact } from "./components/Contact";
import { Footer } from "./components/Footer";
import { useLang } from "./lib/i18n";

function Site() {
  const [data, setData] = useState({ totals: {}, concepts: [], sectors: [], tags: [] });
  const [active, setActive] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [shortlist, setShortlist] = useState(() => {
    try { return JSON.parse(localStorage.getItem("aayat_shortlist") || "[]"); } catch { return []; }
  });
  const lenisRef = useRef(null);
  const { tf } = useLang();

  const toggleShortlist = useCallback((ref) => {
    setShortlist((prev) => {
      const next = prev.includes(ref) ? prev.filter((r) => r !== ref) : [...prev, ref];
      try { localStorage.setItem("aayat_shortlist", JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  useEffect(() => {
    loadConcepts().then(setData).catch((e) => console.error("[concept_data] load failed", e));
  }, []);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  const scrollTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (lenisRef.current) lenisRef.current.scrollTo(el, { offset: -70 });
    else el.scrollIntoView({ behavior: "smooth" });
  }, []);

  const useConcept = useCallback((c) => {
    setActive(null);
    setPrefill({ sector: c.sector, problem: tf("contact.prefill", { title: c.title, ref: c.ref }) });
    scrollTo("contact");
  }, [scrollTo, tf]);

  return (
    <div className="bg-cream min-h-screen">
      <Nav onNavigate={scrollTo} shortlistCount={shortlist.length} />
      <main>
        <Hero totals={data.totals} onNavigate={scrollTo} />
        <Marquee />
        <Matcher concepts={data.concepts} sectors={data.sectors} tags={data.tags} onUseConcept={useConcept} />
        <Library concepts={data.concepts} onOpen={setActive} />
        <Method />
        <Positioning onNavigate={scrollTo} />
        <ArabicCreed onNavigate={scrollTo} />
        <Handover />
        <Contact sectors={data.sectors} prefill={prefill} shortlist={shortlist} />
      </main>
      <Footer />
      {active && <ConceptOverlay concept={active} onClose={() => setActive(null)} onUse={useConcept}
        shortlisted={shortlist.includes(active.ref)} onToggleShortlist={toggleShortlist} />}
      <Toaster position="bottom-right" toastOptions={{
        style: { background: "#071b17", color: "#f3efe6", border: "1px solid rgba(212,181,106,0.4)", borderRadius: 0, fontFamily: "Arial, Helvetica, sans-serif" }
      }} />
    </div>
  );
}

function App() {
  return (
    <LangProvider>
      <Site />
    </LangProvider>
  );
}

export default App;
